/** Where each photograph of a flight was taken, and what the flight was like; clicking a point opens the photograph. */
import { useCallback, useMemo, useState } from 'react';
import { Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/EmptyState';
import { MapView } from '@/map/MapView';
import type { PhotoProperties } from '@/map/photoSource';
import { MapFrame } from './MapFrame';
import { PHOTO_SECTIONS, photoPointsConfig } from './photos/config';
import { PhotoSections } from './photos/PhotoSections';
import { positioningStats } from './photos/positioning';
import { photoStats, tiltExclusion } from './photos/stats';
import type { KindReportProps } from './ReportPage';

export function PhotoPointsReport({ refs, inputs, photos, linkSource, onPhotoError, onVectorError, onVectorProgress, options, onOptionsChange, tokenSource, config }: KindReportProps) {
  const { t } = useTranslation('reports');
  const cfg = useMemo(() => photoPointsConfig(config), [config]);
  const [points, setPoints] = useState<Record<string, PhotoProperties[]>>({});
  const onPhotosLoaded = useCallback((object: string, fc: { features: { properties: PhotoProperties }[] }) => {
    setPoints((prev) => ({ ...prev, [object]: fc.features.map((f) => f.properties) }));
  }, []);
  const { stats, tilt } = useMemo(() => {
    const all = photos.flatMap((ph) => points[ph.object] ?? []);
    const tilt = tiltExclusion(photos.map((ph) => ({ photos: points[ph.object] ?? [], limit: ph.maxTiltDeg })));
    // One limit splits the tilt histogram; acquisitions checked against different ones are not split.
    const tiltLimit = tilt?.limits.length === 1 ? tilt.limits[0] : undefined;
    return { stats: all.length ? photoStats(all, { tiltLimit }) : null, tilt };
  }, [points, photos]);
  const positioning = useMemo(
    () => positioningStats(photos.map((ph) => ({ object: ph.object, photos: points[ph.object] ?? [], positioning: inputs?.find((i) => i.object === ph.object)?.positioning }))),
    [points, photos, inputs],
  );

  if (!PHOTO_SECTIONS.some((s) => cfg.sections[s].enabled)) return <EmptyState title={t('photos.nothingEnabled')} />;

  const map = (
    <MapView
      refs={refs}
      photos={photos}
      linkSource={linkSource}
      onPhotoError={onPhotoError}
      onPhotosLoaded={onPhotosLoaded}
      onVectorError={onVectorError}
      onVectorProgress={onVectorProgress}
      options={options}
      onOptionsChange={onOptionsChange}
      tokenSource={tokenSource}
    />
  );
  return (
    <Stack gap="xl">
      {cfg.sections.map.enabled ? (
        <MapFrame testId="report-photo_points" wide={!!options.wide} onWideChange={(wide) => onOptionsChange({ ...options, wide })}>
          {map}
        </MapFrame>
      ) : (
        // The map still loads the points the sections are computed from; it is just not shown.
        <div data-testid="report-photo_points" style={{ display: 'none' }}>{map}</div>
      )}
      {stats && <PhotoSections stats={stats} config={cfg} tilt={tilt} positioning={positioning} />}
    </Stack>
  );
}
